import { isEmpty } from "lodash-es";
import { type IntlShape, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { InfinitySelect, type InfinitySelectOption } from "~components/InfinitySelect";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { Roles } from "~enums/roles";
import type { Person } from "~types/person";
import type { RoleResponse } from "~types/role.ts";
import type { SelectBaseSingleProperties } from "~types/selectBaseSingleProperties.ts";
import { getRoleTranslationKey } from "~utilities/getRoleTranslationKey";

type Props = SelectBaseSingleProperties<InfinitySelectOption> & {
  seasonId?: string;
  fixtureId?: string;
  roles?: Roles[];
  onChange: (value: unknown) => void;
};
type FormatMessage = IntlShape["formatMessage"];

const getPersonOptions = (response: RoleResponse | undefined, sport: string, formatMessage: FormatMessage) => {
  if (!response) {
    return [];
  }

  const roles = response.data;
  const persons: Person[] = Object.values(response.includes?.resources?.persons || {});

  return persons.map((person) => {
    const role = roles.find((role) => role.personId === person.personId);
    return {
      label: `${person.nameFullLocal} (${formatMessage({ id: `roles.${getRoleTranslationKey(role?.role as Roles, sport)}` })})`,
      value: JSON.stringify({ personId: person.personId, role: role?.role }),
    };
  });
};

const SelectRolePerson = ({ seasonId, fixtureId, roles, value, onChange, ...props }: Props) => {
  const { sport = "", organizationId } = useParams();
  const { formatMessage } = useIntl();

  const onConvertResponseIntoOptionsHandler = (response: RoleResponse) =>
    getPersonOptions(response, sport, formatMessage);

  const url = fixtureId
    ? `${sport}/o/${organizationId}/fixtures/${fixtureId}/roles`
    : `${sport}/o/${organizationId}/roles`;
  const filterParameters: Record<string, string> = {
    ...(seasonId && { seasonId }),
    ...(fixtureId && { fixtureId }),
    ...(roles && { role: roles.join(",") }),
    include: ["persons"].join(","),
  };

  const handleChange = (selectedOption: InfinitySelectOption | null) => {
    if (onChange) {
      onChange({
        ...selectedOption,
        value: JSON.parse(selectedOption?.value || "{}"),
      });
    }
  };

  return (
    <InfinitySelect<RoleResponse>
      {...props}
      onChange={handleChange}
      values={isEmpty(value) || isEmpty(value?.value) ? [] : [value]}
      url={url}
      searchParameter="nameFullLocalContains"
      filterParameters={filterParameters}
      onConvertResponseIntoOptions={onConvertResponseIntoOptionsHandler}
      permissionName={PermissionNames.ROLES}
      permissionOperation={PermissionOperations.READ}
      resetOptionsDependencies={[seasonId, fixtureId, roles]}
    />
  );
};

export default SelectRolePerson;
